import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Input, Select, Btn } from "../components/UI";
import { toast } from "sonner";
import { Megaphone, Search, Loader2, ExternalLink, StickyNote } from "lucide-react";

export default function AdLibraryPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState("");
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    const { data: d } = await api.get(`/political-os/ad-library?q=${encodeURIComponent(q)}&platform=${encodeURIComponent(platform)}`);
    setData(d);
  };
  useEffect(() => { load().catch(() => setData({ ads: [], platforms: [] })); }, [platform]); // eslint-disable-line

  const saveNote = async () => {
    try { await api.post(`/political-os/ad-library/${noteFor}/note`, { note }); toast.success("Research note saved"); setNoteFor(null); setNote(""); load(); }
    catch (e) { toast.error(errMsg(e, "Could not save note")); }
  };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="ad-library-page">
      <PageHeader eyebrow="Public Intelligence" title="Political Ad Library"
        subtitle="Searchable library of publicly available political ads collected via AdLibrary.com — research and annotate, never copy protected creative." />
      <Card>
        <div className="flex flex-wrap gap-3 items-end">
          <Input label="Search" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()}
            placeholder="Message, advertiser or candidate…" className="flex-1 min-w-[220px]" data-testid="adlib-search-input" />
          <Select label="Platform" value={platform} onChange={e => setPlatform(e.target.value)} data-testid="adlib-platform-select">
            <option value="">All platforms</option>
            {data.platforms.map(p => <option key={p} value={p}>{p}</option>)}
          </Select>
          <Btn onClick={load} data-testid="adlib-search-btn"><Search className="w-4 h-4" /> Search</Btn>
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="adlib-results">
        {data.ads.map(ad => (
          <Card key={ad.id} data-testid={`adlib-ad-${ad.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-cyan-400/30 text-cyan-300">{ad.platform}</span>
              <span className="text-sm font-semibold text-white flex-1 min-w-0 truncate">{ad.advertiser || "Unknown advertiser"}</span>
              {ad.days_running && <span className="text-[10px] text-slate-500">{ad.days_running}d running</span>}
            </div>
            <p className="text-xs text-slate-300 mt-2 line-clamp-3">{ad.message || ad.title || "—"}</p>
            <div className="text-[10px] text-slate-600 mt-2">About: {ad.subject_name} · last seen {(ad.last_seen || "").slice(0, 10)}{ad.impressions ? ` · ~${Number(ad.impressions).toLocaleString()} impressions` : ""}</div>
            {(ad.research_notes || []).map((n, i) => (
              <div key={i} className="mt-2 text-[11px] text-amber-200/90 bg-amber-400/[0.06] border border-amber-400/20 rounded-lg px-3 py-1.5">
                <StickyNote className="w-3 h-3 inline mr-1" />{n.note} <span className="text-slate-600">— {n.by}</span>
              </div>
            ))}
            <div className="flex gap-2 mt-3">
              {ad.url && <a href={ad.url} target="_blank" rel="noreferrer" className="text-xs text-cyan-300 hover:underline inline-flex items-center gap-1">View ad <ExternalLink className="w-3 h-3" /></a>}
              <button onClick={() => { setNoteFor(ad.id); setNote(""); }} data-testid={`adlib-note-btn-${ad.id}`}
                className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><StickyNote className="w-3 h-3" /> Add note</button>
            </div>
            {noteFor === ad.id && (
              <div className="flex gap-2 mt-2">
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="Research note…" data-testid="adlib-note-input"
                  className="flex-1 bg-slate-900/80 border border-white/15 rounded-lg px-3 py-1.5 text-xs focus:border-cyan-400/60 focus:outline-none" />
                <button onClick={saveNote} data-testid="adlib-note-save" className="text-xs px-3 py-1.5 rounded-lg border border-cyan-400/40 text-cyan-300">Save</button>
              </div>
            )}
          </Card>
        ))}
        {data.ads.length === 0 && <Card><div className="text-sm text-slate-500 py-4 text-center">No ads found — sync live ads from a tracked race's Opposition tab first.</div></Card>}
      </div>
      <p className="text-[10px] text-slate-600">{data.note}</p>
    </div>
  );
}
